import { useMemo, useRef } from 'react';
import {
  useLocationFieldConfig,
  useOfficeFieldConfig,
  useNoteTakerConfig,
  useDurationFieldConfig,
  useAccountUsersFieldConfig,
  useMeetingRoomFieldConfig,
} from '@/formConfigs';
import { useInterviewerField } from '../useInterviewerField';
import type { ScheduleMode, ApiReference } from '../../../../config/types';
import {
  isAdhocMode,
  isManualMode,
  isSelfScheduleMode,
} from '../../../../config/modeClassifiers';
import { useHackerRankTemplates } from '../../../../../InterviewActivity/details/useHackerRankTemplates';
import { useSuggestedInterviewer } from '@/api/suggestedInterviewer';
import { useScheduleCalendarData } from '../useScheduleCalendarData';
import { useTimeOptions } from './useTimeOptions';
import { buildInterviewDetailsSchema } from './interviewDetailsSchema';
import { buildScheduleDetailsSchema } from './scheduleDetailsSchema';
import {
  renderMeetingRoomGroupHeader,
  renderMeetingRoomOption,
  renderMeetingRoomTag,
  renderMeetingRoomTriggerValue,
} from '../../fields/MeetingRoomRenders';
import { copy } from './constants';
import type { UseDetailsFormSchemaOptions } from './types';

export function useDetailsFormSchema(
  modeOrOptions: ScheduleMode | UseDetailsFormSchemaOptions,
  apiReferenceArg?: ApiReference
) {
  // Support both old call signature (mode, apiReference) and options object.
  const options: UseDetailsFormSchemaOptions =
    typeof modeOrOptions === 'string'
      ? { mode: modeOrOptions, apiReference: apiReferenceArg }
      : modeOrOptions;

  const {
    mode,
    apiReference = 'create',
    interviewPlan,
    meetingRoomsEnabled = false,
    interviewType = 'one_on_one',
    selectedStartDate = null,
    selectedStartTime = '',
    scheduleTimezone,
    calendarBookedStartIso = null,
    calendarBookedEndIso = null,
    visibleWeekStartIso = null,
    disconnectedInterviewerIds,
    meetingRoomGroups,
    meetingRoomsLoading = false,
    candidateName,
    candidateAvatar,
    candidatePhone,
    atsJobStageActionId,
    hasInterviewerSelected = false,
    participantDefaultOptions,
    findTimeProps,
    bufferProps,
    onInterviewerRecommend,
  } = options;

  const scheduleTz =
    scheduleTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // ── Mode flags ──────────────────────────────────────────────────────────
  const isManualOrAdhoc = isManualMode(mode);
  const isSelfSchedule = isSelfScheduleMode(mode);
  const isAdhoc = isAdhocMode(mode);
  const isEdit = apiReference === 'edit';
  const isMultiInterviewer = isSelfSchedule || interviewType === 'panel';

  // HackerRank: plan carries location='hackerrank' for HR interviews.
  const isHackerRank =
    (interviewPlan?.location as string | undefined) === 'hackerrank';

  // ── Sub-hooks ────────────────────────────────────────────────────────────
  const { templateOptions: hackerRankTemplateOptions, isLoading: hrLoading } =
    useHackerRankTemplates({ enabled: isHackerRank });

  // AI-recommended interviewer — manual one-on-one create from plan only.
  // Fires immediately on mount (no slot required). Uses stable placeholder
  // times via ref so the query key doesn't change every render.
  //
  // Stops firing once the user has picked an interviewer — at that point
  // the recommendation pill is hidden and the query was the only thing
  // still refetching as `calendarBookedStartIso`/`endIso` (slot drag,
  // resize) or other deps churned. Without this gate the network tab
  // shows a fresh `/recommend` call every time the user moves the booked
  // slot even though nothing is consuming the result.
  const hasBookedSlot = Boolean(calendarBookedStartIso && calendarBookedEndIso);
  const canRecommend =
    !isEdit &&
    mode === 'manual' &&
    !isAdhoc &&
    interviewType === 'one_on_one' &&
    typeof atsJobStageActionId === 'number' &&
    atsJobStageActionId > 0 &&
    !hasInterviewerSelected;

  const placeholderTimesRef = useRef<{ start: string; end: string } | null>(
    null
  );
  if (!placeholderTimesRef.current) {
    const now = Date.now();
    placeholderTimesRef.current = {
      start: new Date(now).toISOString(),
      end: new Date(now + 30 * 60 * 1000).toISOString(),
    };
  }
  const recommendStartAt =
    calendarBookedStartIso ?? placeholderTimesRef.current.start;
  const recommendEndAt =
    calendarBookedEndIso ?? placeholderTimesRef.current.end;

  const { data: recommendedUser, isLoading: isRecommending } =
    useSuggestedInterviewer({
      enabled: canRecommend,
      atsJobStageActionId: atsJobStageActionId ?? 0,
      startAt: recommendStartAt,
      endAt: recommendEndAt,
      isTimeslot: hasBookedSlot,
      timeZone: scheduleTz,
    });

  const calendarData = useScheduleCalendarData({ interviewPlan, apiReference });
  const shouldShowCalendarRow = calendarData.shouldShowRow;

  const interviewerField = useInterviewerField({
    label: isMultiInterviewer
      ? copy.interviewer.labelPlural
      : copy.interviewer.label,
    placeholder: copy.interviewer.placeholder,
    isMulti: isMultiInterviewer,
    defaultOptions: participantDefaultOptions,
    calendarBookedStartIso,
    scheduleTimezone: scheduleTz,
    atsJobStageActionId,
    visibleWeekStartIso,
    disconnectedInterviewerIds,
  });

  const { fieldConfig: locationField } = useLocationFieldConfig({
    isHackerRank,
    config: {
      label: copy.location.label,
      required: true,
      condition: {
        operator: 'isNotEmpty',
        field: 'participant_ids',
        preserveValue: true,
      },
    },
  });

  const { fieldConfig: officeField } = useOfficeFieldConfig({
    config: { name: 'ats_office_id' },
  });

  const noteTakerField = useNoteTakerConfig();

  const { fieldConfig: durationField } = useDurationFieldConfig({
    name: 'duration',
    label: copy.duration.label,
    unitLabel: copy.duration.unit,
  });

  const { fieldConfig: hostField } = useAccountUsersFieldConfig({
    name: 'host_id',
    label: copy.host.label,
    isMulti: false,
    placeholder: 'Select host',
    condition: {
      operator: 'equal',
      field: 'location',
      value: 'zoom',
    },
  });

  const { fieldConfig: meetingRoomField } = useMeetingRoomFieldConfig({
    name: 'meeting_room_ids',
    label: copy.meetingRoom.label,
    isMulti: false,
    placeholder: 'Search meeting rooms',
    condition: {
      operator: 'equal',
      field: 'book_meeting_room',
      value: true,
    },
    groupedOptions: meetingRoomGroups,
    loading: meetingRoomsLoading,
    showOptionDividers: true,
    renderOption: renderMeetingRoomOption,
    renderGroupHeader: renderMeetingRoomGroupHeader,
    renderTriggerValue: renderMeetingRoomTriggerValue,
    renderTag: renderMeetingRoomTag,
  });

  const { startTimeOptions, endTimeOptions, datePickerMinDate } =
    useTimeOptions({
      selectedStartDate,
      selectedStartTime,
      calendarBookedStartIso,
      calendarBookedEndIso,
      scheduleTz,
    });

  // ── Schema composition ────────────────────────────────────────────────────
  return useMemo(
    () => [
      ...buildInterviewDetailsSchema({
        interviewPlan,
        interviewerField,
        meetingRoomField,
        meetingRoomsEnabled,
        isMultiInterviewer,
        isSelfSchedule,
        canRecommend,
        isRecommending,
        recommendedUser,
        onInterviewerRecommend,
      }),
      ...buildScheduleDetailsSchema({
        isManualOrAdhoc,
        isSelfSchedule,
        apiReference,
        calendarData,
        shouldShowCalendarRow,
        durationField,
        locationField,
        hostField,
        officeField,
        noteTakerField,
        hackerRankTemplateOptions,
        hrLoading,
        startTimeOptions,
        endTimeOptions,
        datePickerMinDate,
        candidateName,
        candidateAvatar,
        candidatePhone,
        findTimeProps,
        bufferProps,
      }),
    ],
    [
      interviewPlan,
      meetingRoomsEnabled,
      meetingRoomField,
      interviewerField,
      durationField,
      isManualOrAdhoc,
      datePickerMinDate,
      startTimeOptions,
      endTimeOptions,
      locationField,
      hostField,
      officeField,
      noteTakerField,
      isMultiInterviewer,
      isSelfSchedule,
      apiReference,
      hackerRankTemplateOptions,
      hrLoading,
      candidateName,
      candidateAvatar,
      candidatePhone,
      canRecommend,
      isRecommending,
      recommendedUser,
      onInterviewerRecommend,
      shouldShowCalendarRow,
      calendarData,
      findTimeProps,
      bufferProps,
    ]
  );
}
